import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import Navigation from '../components/Navigation';
import { Sparkles, ArrowRight, Search, LayoutDashboard, CreditCard, MessageSquare, Calendar, Shield, Clock, Lock, Gift, CheckCircle } from 'lucide-react';
import { getConciergeAvailability } from '../services/conciergeAvailability';

// Product Preview Component - Shows what Celey looks like
const ProductPreview = () => (
  <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-lg">
    {/* Phone frame effect */}
    <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
      <div className="bg-white rounded-[1.5rem] overflow-hidden">
        {/* Mini app header */}
        <div className="bg-emerald-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-white" />
            <span className="text-white font-semibold text-sm">Celey</span>
          </div>
          <div className="flex gap-1">
            <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
            <div className="w-2 h-2 rounded-full bg-emerald-300"></div>
          </div>
        </div>
        
        {/* 3-panel preview */}
        <div className="grid grid-cols-3 divide-x divide-gray-100">
          {/* Messages panel */}
          <div className="p-3 text-center">
            <MessageSquare className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-900">Messages</p>
            <div className="mt-2 space-y-1">
              <div className="h-1.5 bg-gray-200 rounded w-full"></div>
              <div className="h-1.5 bg-emerald-200 rounded w-3/4"></div>
              <div className="h-1.5 bg-gray-200 rounded w-5/6"></div>
            </div>
            <div className="mt-2 bg-emerald-100 rounded px-1 py-0.5">
              <span className="text-[8px] text-emerald-700 font-medium">3 new</span>
            </div>
          </div>
          
          {/* Calendar panel */}
          <div className="p-3 text-center">
            <Calendar className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-900">Calendar</p>
            <div className="mt-2 grid grid-cols-4 gap-0.5">
              {[...Array(12)].map((_, i) => (
                <div 
                  key={i} 
                  className={`h-2 w-2 rounded-sm ${i === 5 || i === 8 ? 'bg-emerald-500' : 'bg-gray-100'}`}
                ></div>
              ))}
            </div>
            <div className="mt-2 bg-emerald-100 rounded px-1 py-0.5">
              <span className="text-[8px] text-emerald-700 font-medium">2 booked</span>
            </div>
          </div>
          
          {/* Payments panel */}
          <div className="p-3 text-center">
            <CreditCard className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
            <p className="text-[10px] font-medium text-gray-900">Payments</p>
            <div className="mt-2">
              <div className="text-sm font-bold text-gray-900">$850</div>
              <div className="text-[8px] text-gray-500">this month</div>
            </div>
            <div className="mt-1 bg-emerald-100 rounded px-1 py-0.5">
              <span className="text-[8px] text-emerald-700 font-medium">Stripe</span>
            </div>
          </div>
        </div>
        
        {/* Bottom indicator */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
          <div className="h-1 w-12 bg-gray-300 rounded-full mx-auto"></div>
        </div>
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showPricingModal, setShowPricingModal] = useState(false);
  // Phase 4 — Concierge fork visibility.
  const [conciergeEnabled, setConciergeEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getConciergeAvailability().then((res) => {
      if (!cancelled) setConciergeEnabled(!!res?.enabled);
    });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (user) {
      if (user.role === 'vendor') {
        navigate('/vendor/dashboard');
      } else if (user.role === 'customer') {
        navigate('/customer-dashboard', { replace: true });
      } else if (user.role === 'admin') {
        navigate('/admin');
      }
    }
  }, [user, navigate]);

  if (user) {
    return null;
  }

  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen-safe bg-white flex flex-col">
      <Navigation showFeedback={true} />

      {/* Hero Section - Optimized for TikTok traffic */}
      <section className="pt-12 sm:pt-20 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-emerald-50 via-emerald-50/50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left: Copy */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 leading-[1.1] mb-4">
                Plan your event.
                <br />
                <span className="text-emerald-600">Book trusted local vendors in one place.</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-3 max-w-xl mx-auto lg:mx-0">
                Get discovered locally. Manage inquiries. Accept payments. Keep your calendar straight.
              </p>
              
              {/* TikTok validation line */}
              <p className="text-sm text-gray-500 mb-6 max-w-xl mx-auto lg:mx-0">
                If your bookings live in DMs, texts, Venmo, and a calendar you hope is right — Celey organizes it.
              </p>
              
              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-4">
                <Button 
                  size="lg" 
                  className="text-base px-6 py-6 rounded-full bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-600/20"
                  onClick={() => navigate('/plan-event')}
                  data-testid="hero-plan-event-btn"
                >
                  Plan My Event <ArrowRight className="ml-1 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-base px-6 py-6 rounded-full border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                  onClick={() => navigate('/auth?role=vendor&mode=signup')}
                  data-testid="hero-provider-btn"
                >
                  Join as a Service Provider <span className="text-emerald-400 font-normal ml-1">— It&apos;s Free</span>
                </Button>
              </div>
              
              {/* Phase 4 — Concierge fork. Subtle, additive link under the
                  primary CTA. Only rendered when ENABLE_CONCIERGE_FLOW is on,
                  so the legacy hero is unchanged when off. */}
              {conciergeEnabled && (
                <div className="mb-4" data-testid="landing-concierge-fork">
                  <button
                    type="button"
                    onClick={() => navigate('/concierge/intake')}
                    className="inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-800 underline-offset-4 hover:underline"
                    data-testid="landing-concierge-cta"
                  >
                    <Sparkles className="w-4 h-4" />
                    Or — let Celey plan it for you
                  </button>
                </div>
              )}
              
              {/* Microcopy */}
              <p className="text-xs text-gray-400">
                Free to join. No contracts. $0 platform fees for your first 3 months.
              </p>
            </div>
            
            {/* Right: Product Preview */}
            <div className="order-first lg:order-last">
              <ProductPreview />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Punchier */}
      <section id="how-it-works" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              How Celey Works
            </h2>
            <p className="text-gray-500">Three steps. Zero chaos.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {/* Step 1 */}
            <div className="text-center p-6 rounded-2xl bg-gray-50/80 border border-gray-100" data-testid="step-1">
              <div className="bg-emerald-600 w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <Search className="h-6 w-6 text-white" />
              </div>
              <div className="text-[11px] font-medium text-emerald-600/70 uppercase tracking-wider mb-2">Step 1</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Get discovered</h3>
              <p className="text-sm text-gray-600 mb-3">
                Customers find you without chasing DMs.
              </p>
              <span className="inline-block text-[10px] text-gray-400 px-2 py-0.5 rounded">
                replaces social media DMs
              </span>
            </div>

            {/* Step 2 */}
            <div className="text-center p-6 rounded-2xl bg-gray-50/80 border border-gray-100" data-testid="step-2">
              <div className="bg-emerald-600 w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <div className="text-[11px] font-medium text-emerald-600/70 uppercase tracking-wider mb-2">Step 2</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manage in one place</h3>
              <p className="text-sm text-gray-600 mb-3">
                Inquiries, messages, calendar — organized.
              </p>
              <span className="inline-block text-[10px] text-gray-400 px-2 py-0.5 rounded">
                replaces scattered notes
              </span>
            </div>

            {/* Step 3 */}
            <div className="text-center p-6 rounded-2xl bg-gray-50/80 border border-gray-100" data-testid="step-3">
              <div className="bg-emerald-600 w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <div className="text-[11px] font-medium text-emerald-600/70 uppercase tracking-wider mb-2">Step 3</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Lock in bookings</h3>
              <p className="text-sm text-gray-600 mb-3">
                Accept deposits and secure the date.
              </p>
              <span className="inline-block text-[10px] text-gray-400 px-2 py-0.5 rounded">
                replaces Venmo chaos
              </span>
            </div>
          </div>
          
          {/* Stripe trust cue */}
          <div className="text-center mt-10">
            <p className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
              <Lock className="h-3 w-3" /> Secure payments powered by Stripe.
            </p>
          </div>
        </div>
      </section>

      {/* Plan Your Event in Minutes - Customer CTA */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-emerald-600 to-emerald-700">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
            <div className="text-center sm:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                Plan Your Event in Minutes
              </h2>
              <p className="text-emerald-100 text-sm sm:text-base">
                Send inquiries and organize your event in minutes.
              </p>
            </div>
            <Button 
              size="lg"
              onClick={() => navigate('/plan-event')}
              className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg px-6 py-5 rounded-full font-semibold whitespace-nowrap"
              data-testid="plan-event-cta"
            >
              Plan My Event <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits - Stronger outcomes */}
      <section className="py-14 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50/70" data-testid="benefits-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Why service providers switch to Celey
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100">
              <div className="bg-emerald-50 p-2.5 rounded-lg flex-shrink-0">
                <MessageSquare className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-0.5">Stop losing inquiries</h3>
                <p className="text-sm text-gray-500 leading-snug">No more DMs buried in your inbox.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100">
              <div className="bg-emerald-50 p-2.5 rounded-lg flex-shrink-0">
                <Calendar className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-0.5">Prevent double bookings</h3>
                <p className="text-sm text-gray-500 leading-snug">One calendar. Always current.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100">
              <div className="bg-emerald-50 p-2.5 rounded-lg flex-shrink-0">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-0.5">Look instantly professional</h3>
                <p className="text-sm text-gray-500 leading-snug">A real profile builds trust fast.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white p-5 rounded-xl border border-gray-100">
              <div className="bg-emerald-50 p-2.5 rounded-lg flex-shrink-0">
                <Clock className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-0.5">Save hours every week</h3>
                <p className="text-sm text-gray-500 leading-snug">Less admin. More doing.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience Clarification */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8 bg-white border-y border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base sm:text-lg text-gray-600">
            <span className="font-semibold text-gray-900">Built for event service providers</span> — balloon artists, decorators, DJs, photographers, bakers, and more.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-emerald-600" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to organize your bookings?
          </h2>
          <p className="text-emerald-100 mb-8">
            Join for free. Start getting discovered.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-emerald-600 hover:bg-emerald-50 text-base px-8 py-6 rounded-full font-semibold shadow-lg"
            onClick={() => navigate('/auth?role=vendor&mode=signup')}
            data-testid="final-cta-btn"
          >
            Join as a Service Provider <span className="text-emerald-400 font-normal ml-1">— It&apos;s Free</span> <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
          <p className="text-xs text-emerald-200/80 mt-5">
            Free to join. No contracts. $0 platform fees for your first 3 months.
          </p>
        </div>
      </section>

      {/* For Customers */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-gray-600 mb-3">
            <span className="font-medium">Looking for event services?</span> Browse and send inquiries for free.
          </p>
          <Button 
            variant="outline"
            className="border-gray-300 text-gray-700 hover:bg-white rounded-full"
            onClick={() => navigate('/browse')}
            data-testid="browse-providers-btn"
          >
            Browse Service Providers
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5 text-emerald-400" />
              <span className="text-xl font-bold">Celey</span>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-6 text-sm">
              <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white transition-colors">
                Support
              </button>
              <button onClick={() => navigate('/browse')} className="text-gray-400 hover:text-white transition-colors">
                Browse
              </button>
              <button onClick={() => navigate('/faqs')} className="text-gray-400 hover:text-white transition-colors">
                FAQs
              </button>
              <button onClick={() => navigate('/auth')} className="text-gray-400 hover:text-white transition-colors">
                Sign In
              </button>
            </div>
            <div className="text-gray-500 text-xs">
              © 2024 Celey
            </div>
          </div>
        </div>
      </footer>

      {/* How Provider Pricing Works Modal */}
      <Dialog open={showPricingModal} onOpenChange={setShowPricingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Gift className="h-5 w-5 text-emerald-600" />
              How Provider Pricing Works
            </DialogTitle>
            <DialogDescription>
              Simple, transparent pricing for service providers
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Start with 3 months free</p>
                <p className="text-sm text-gray-500">New providers enjoy $0 platform fees for their first 3 months.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <Gift className="h-4 w-4 text-emerald-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Extend with referrals</p>
                <p className="text-sm text-gray-500">Refer other providers to Celey and earn additional months of $0 platform fees.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">After your free period</p>
                <p className="text-sm text-gray-500">Standard platform fees apply only after your free period ends. You keep 100% of your service fees.</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button 
              onClick={() => {
                setShowPricingModal(false);
                navigate('/auth?role=vendor&mode=signup');
              }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Get Started Free <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
